import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { DEFAULT_LOCALE } from '@/utils/i18n/supportedLocales';
import { LanguageCode } from '@heidi/schema/ml-scribe';
import { getCountry } from '@heidi/utils/get-country';
import { getAvailableDisplayLanguagesForCountry } from '@heidi/utils/i18n';

/**
 * Display language inferred from the visitor's timezone country.
 *
 * The timezone is the weakest language signal available: an explicit locale
 * prefix in the URL (`/fr-FR/onboarding`), the browser's Accept-Language
 * (negotiated into the URL prefix by the middleware), and the OS language on
 * desktop all outrank it. So this returns `undefined` — leaving the current
 * locale untouched — whenever the app already resolved to a non-default
 * locale.
 */
export function useTimezoneLanguageDefault(): LanguageCode | undefined {
  const { locale } = useIntl();
  const timezoneLanguage = useMemo(() => {
    const country = getCountry();
    const [primaryLang] = getAvailableDisplayLanguagesForCountry(country?.countryCode);
    return primaryLang ?? LanguageCode.en;
  }, []);
  return locale === DEFAULT_LOCALE.toString() ? timezoneLanguage : undefined;
}
